import { supabase } from "@/integrations/supabase/client";

export class ProactiveTokenManager {
  private static instance: ProactiveTokenManager;
  
  private constructor() {}
  
  public static getInstance(): ProactiveTokenManager {
    if (!ProactiveTokenManager.instance) {
      ProactiveTokenManager.instance = new ProactiveTokenManager();
    }
    return ProactiveTokenManager.instance;
  }

  /**
   * Check if a token needs refresh (expires within 10 minutes)
   * This ensures proactive refresh before actual expiration
   */
  public static needsRefresh(expiresAt: string): boolean {
    const now = new Date().getTime();
    const expiryTime = new Date(expiresAt).getTime();
    const tenMinutes = 10 * 60 * 1000; // 10 minutes in milliseconds
    
    return (expiryTime - now) <= tenMinutes;
  }

  /**
   * Check if a token is expired
   */
  public static isExpired(expiresAt: string): boolean {
    const now = new Date().getTime();
    const expiryTime = new Date(expiresAt).getTime();
    
    return expiryTime <= now;
  }

  /**
   * Proactively refresh tokens for all connections of a user if needed
   */
  public static async ensureValidToken(userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log(`🔍 Checking token validity for all connections of user: ${userId}`);
      
      const { data: connections, error } = await supabase
        .from("calendar_connections")
        .select("*")
        .eq("user_id", userId)
        .eq("provider", "google");

      if (error || !connections || connections.length === 0) {
        console.error(`❌ No calendar connections found for user ${userId}:`, error);
        return { success: false, error: 'No calendar connections found' };
      }

      console.log(`Found ${connections.length} calendar connection(s) for user ${userId}`);

      // Check and refresh each connection if needed
      const refreshPromises = connections.map(async (connection) => {
        // Check if token needs refresh
        if (!this.needsRefresh(connection.expires_at)) {
          console.log(`✅ Token is still valid for connection ${connection.id} (${connection.email})`);
          return { success: true, connectionId: connection.id };
        }

        console.log(`🔄 Token needs refresh for connection ${connection.id} (${connection.email}), attempting refresh...`);
        
        // Call the Google Calendar function to refresh token
        const { data, error: refreshError } = await supabase.functions.invoke("google-calendar", {
          body: { 
            action: "refresh_token",
            userId: userId,
            connectionId: connection.id // Pass specific connection ID if supported
          },
        });

        if (refreshError || !data?.success) {
          console.error(`❌ Failed to refresh token for connection ${connection.id}:`, refreshError || data?.error);
          
          const newFailureCount = (connection.refresh_failure_count || 0) + 1;
          const isCriticalFailure = newFailureCount >= 10; // Only notify after 10 consecutive failures
          
          // Update failure count for this specific connection
          try {
            await supabase
              .from('calendar_connections')
              .update({
                last_refresh_attempt: new Date().toISOString(),
                refresh_failure_count: newFailureCount,
                token_health_status: isCriticalFailure ? 'error' : 'warning',
                updated_at: new Date().toISOString()
              })
              .eq('id', connection.id);
          } catch (updateError) {
            console.error(`Failed to update failure count for connection ${connection.id}:`, updateError);
          }
          
          // Only send notifications if automatic refresh system has genuinely failed AND it's been persistent
          if (isCriticalFailure && newFailureCount >= 25) { // Much higher threshold - 25+ failures
            console.error(`🚨 Automatic refresh system failed after ${newFailureCount} attempts for connection ${connection.id}`);
            try {
              const { CalendarNotificationService } = await import('./notification-integration');
              await CalendarNotificationService.notifyRefreshFailed(
                userId,
                connection.email || 'calendar@unknown.com',
                `Automatic token refresh failed after ${newFailureCount} attempts for calendar ${connection.email}: ${refreshError?.message || data?.error}`
              );
            } catch (notificationError) {
              console.error('Failed to send failure notification:', notificationError);
            }
          } else {
            console.log(`⚠️ Token refresh failed for connection ${connection.id} (${newFailureCount}/10), will retry automatically`);
          }
          
          return { 
            success: false, 
            connectionId: connection.id,
            error: refreshError?.message || data?.error || 'Token refresh failed' 
          };
        }

        console.log(`✅ Successfully refreshed token for connection ${connection.id} (${connection.email})`);
        
        // If we had previous failures, notify that the system has recovered
        if ((connection.refresh_failure_count || 0) >= 3) {
          console.log(`🎉 Automatic refresh system recovered for connection ${connection.id} after ${connection.refresh_failure_count} previous failures`);
          try {
            const { CalendarNotificationService } = await import('./notification-integration');
            await CalendarNotificationService.notifyConnectionRestored(
              userId,
              connection.email || 'calendar@unknown.com'
            );
          } catch (notificationError) {
            console.error('Failed to send recovery notification:', notificationError);
          }
        }
        
        // Update success status for this specific connection
        try {
          await supabase
            .from('calendar_connections')
            .update({
              last_refresh_attempt: new Date().toISOString(),
              refresh_failure_count: 0,
              token_health_status: 'healthy',
              updated_at: new Date().toISOString()
            })
            .eq('id', connection.id);
        } catch (updateError) {
          console.error(`Failed to update success status for connection ${connection.id}:`, updateError);
        }

        return { success: true, connectionId: connection.id };
      });

      // Wait for all refresh operations to complete
      const results = await Promise.all(refreshPromises);
      
      // Check if all connections are healthy
      const failedConnections = results.filter(r => !r.success);
      const successfulConnections = results.filter(r => r.success);
      
      console.log(`✅ Token refresh results: ${successfulConnections.length} successful, ${failedConnections.length} failed`);

      // Return success if at least one connection is healthy
      if (successfulConnections.length > 0) {
        return { success: true };
      } else {
        return { 
          success: false, 
          error: `All ${connections.length} calendar connections failed to refresh` 
        };
      }
    } catch (error) {
      console.error(`💥 Error ensuring valid tokens for user ${userId}:`, error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Test if the calendar API is actually working and update health status accordingly for all connections
   */
  public static async validateAndFixTokenHealth(userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log(`🧪 Testing calendar API functionality for all connections of user: ${userId}`);
      
      // Get all connections for this user
      const { data: connections, error } = await supabase
        .from("calendar_connections")
        .select("*")
        .eq("user_id", userId)
        .eq("provider", "google");

      if (error || !connections || connections.length === 0) {
        console.log(`❌ No calendar connections found for user ${userId}:`, error);
        return { success: false, error: error?.message || 'No calendar connections found' };
      }

      console.log(`Testing ${connections.length} calendar connection(s) for user ${userId}`);

      // Test each connection
      const testResults = await Promise.all(connections.map(async (connection) => {
        try {
          // Test the calendar API by making a simple list calendars call
          const { data, error: apiError } = await supabase.functions.invoke("google-calendar", {
            body: { 
              action: "list_calendars",
              userId: userId
            },
          });

          if (apiError || !data?.success) {
            console.log(`❌ Calendar API test failed for connection ${connection.id} (${connection.email}):`, apiError || data?.error);
            return { success: false, connectionId: connection.id, error: apiError?.message || data?.error };
          }

          console.log(`✅ Calendar API is working for connection ${connection.id} (${connection.email})`);
          
          // If API is working but we have failures recorded, reset the health status
          const hasFailures = (connection.refresh_failure_count || 0) > 0;
          const isWarningStatus = connection.token_health_status === 'warning';
          
          if (hasFailures || isWarningStatus) {
            console.log(`🔄 Resetting health status for connection ${connection.id} - failures: ${connection.refresh_failure_count}, status: ${connection.token_health_status}`);
            
            try {
              const { error: updateError } = await supabase
                .from('calendar_connections')
                .update({
                  token_health_status: 'healthy',
                  refresh_failure_count: 0,
                  last_refresh_attempt: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                })
                .eq('id', connection.id);
                
              if (updateError) {
                console.error(`Failed to update token health status for connection ${connection.id}:`, updateError);
                return { success: false, connectionId: connection.id, error: 'Failed to update health status' };
              }
              
              console.log(`✅ Token health status reset to healthy for connection ${connection.id} - API is functional`);
            } catch (updateError) {
              console.error(`Error updating token health for connection ${connection.id}:`, updateError);
              return { success: false, connectionId: connection.id, error: 'Database update failed' };
            }
          } else {
            console.log(`✅ API is working and health status is already good for connection ${connection.id}`);
          }
          
          return { success: true, connectionId: connection.id };
        } catch (connectionError) {
          console.error(`Error testing connection ${connection.id}:`, connectionError);
          return { success: false, connectionId: connection.id, error: connectionError.message };
        }
      }));

      // Clear the calendar connection cache so the UI reflects the updated health status
      const { CalendarConnectionManager } = await import('./connection-manager');
      CalendarConnectionManager.clearCache();

      const successfulTests = testResults.filter(r => r.success);
      const failedTests = testResults.filter(r => !r.success);
      
      console.log(`📊 API test results: ${successfulTests.length} successful, ${failedTests.length} failed`);

      // Return success if at least one connection is healthy
      if (successfulTests.length > 0) {
        return { success: true };
      } else {
        const errors = failedTests.map(r => r.error).join(', ');
        return { success: false, error: `All connections failed API test: ${errors}` };
      }
    } catch (error) {
      console.error(`💥 Error validating token health for user ${userId}:`, error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Get token health status for all connections of a user
   */
  public static async getTokenHealth(userId: string): Promise<{
    isHealthy: boolean;
    status: string;
    expiresAt: string | null;
    lastRefreshAttempt: string | null;
    failureCount: number;
    connections: Array<{
      id: string;
      email: string;
      isHealthy: boolean;
      status: string;
      expiresAt: string;
      failureCount: number;
    }>;
  }> {
    try {
      const { data: connections, error } = await supabase
        .from("calendar_connections")
        .select("id, email, expires_at, token_health_status, last_refresh_attempt, refresh_failure_count")
        .eq("user_id", userId)
        .eq("provider", "google");

      if (error || !connections || connections.length === 0) {
        return {
          isHealthy: false,
          status: 'no_connection',
          expiresAt: null,
          lastRefreshAttempt: null,
          failureCount: 0,
          connections: []
        };
      }

      const connectionHealths = connections.map(connection => {
        const isExpired = this.isExpired(connection.expires_at);
        const needsRefresh = this.needsRefresh(connection.expires_at);
        const hasFailures = (connection.refresh_failure_count || 0) > 0;
        
        // Token is healthy if not expired, no recent failures, and database status is healthy
        const isHealthy = !isExpired && 
                          !hasFailures && 
                          (connection.token_health_status === 'healthy' || 
                           connection.token_health_status === null);

        // Determine status
        let status = 'healthy';
        if (isExpired) {
          status = 'expired';
        } else if (hasFailures) {
          status = 'warning';
        } else if (needsRefresh) {
          status = 'needs_refresh';
        }

        return {
          id: connection.id,
          email: connection.email,
          isHealthy,
          status,
          expiresAt: connection.expires_at,
          failureCount: connection.refresh_failure_count || 0
        };
      });

      // Overall health is good if at least one connection is healthy
      const healthyConnections = connectionHealths.filter(c => c.isHealthy);
      const overallHealthy = healthyConnections.length > 0;
      
      // Overall status based on worst case
      let overallStatus = 'healthy';
      if (connectionHealths.every(c => c.status === 'expired')) {
        overallStatus = 'expired';
      } else if (connectionHealths.some(c => c.status === 'warning')) {
        overallStatus = 'warning';
      } else if (connectionHealths.some(c => c.status === 'needs_refresh')) {
        overallStatus = 'needs_refresh';
      }

      // Use the primary connection or first connection for main status
      const primaryConnection = connections[0];
      
      console.log(`📊 Token health check for user ${userId}:`, {
        totalConnections: connections.length,
        healthyConnections: healthyConnections.length,
        overallHealthy,
        overallStatus,
        connectionDetails: connectionHealths
      });

      return {
        isHealthy: overallHealthy,
        status: overallStatus,
        expiresAt: primaryConnection.expires_at,
        lastRefreshAttempt: primaryConnection.last_refresh_attempt || null,
        failureCount: Math.max(...connectionHealths.map(c => c.failureCount)),
        connections: connectionHealths
      };
    } catch (error) {
      console.error('Error getting token health:', error);
      return {
        isHealthy: false,
        status: 'error',
        expiresAt: null,
        lastRefreshAttempt: null,
        failureCount: 0,
        connections: []
      };
    }
  }

  /**
   * Manually trigger a token refresh for all connections of a specific user
   */
  public static async manualRefresh(userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log(`🔄 Manual token refresh triggered for all connections of user: ${userId}`);
      
      const { data: connections, error } = await supabase
        .from("calendar_connections")
        .select("*")
        .eq("user_id", userId)
        .eq("provider", "google");

      if (error || !connections || connections.length === 0) {
        console.error(`❌ No calendar connections found for user ${userId}:`, error);
        return { success: false, error: 'No calendar connections found' };
      }

      console.log(`Manually refreshing ${connections.length} connection(s) for user ${userId}`);

      // Refresh all connections in parallel
      const refreshPromises = connections.map(async (connection) => {
        try {
          // Use the regular google-calendar function for manual refresh
          const { data, error } = await supabase.functions.invoke("google-calendar", {
            body: { 
              action: "refresh_token",
              userId: userId,
              connectionId: connection.id
            },
          });

          if (error || !data?.success) {
            console.error(`❌ Manual token refresh failed for connection ${connection.id}:`, error || data?.error);
            return { 
              success: false, 
              connectionId: connection.id,
              error: error?.message || data?.error || 'Manual token refresh failed' 
            };
          }

          console.log(`✅ Manual token refresh successful for connection ${connection.id} (${connection.email})`);
          
          // After successful refresh, update the token health status
          try {
            const { error: updateError } = await supabase
              .from('calendar_connections')
              .update({
                last_refresh_attempt: new Date().toISOString(),
                refresh_failure_count: 0,
                token_health_status: 'healthy',
                updated_at: new Date().toISOString()
              })
              .eq('id', connection.id);
              
            if (updateError) {
              console.error(`Failed to update token health status for connection ${connection.id}:`, updateError);
            } else {
              console.log(`✅ Token health status updated to healthy for connection ${connection.id}`);
            }
          } catch (updateError) {
            console.error(`Error updating token health status for connection ${connection.id}:`, updateError);
          }
          
          return { success: true, connectionId: connection.id };
        } catch (connectionError) {
          console.error(`Error refreshing connection ${connection.id}:`, connectionError);
          return { 
            success: false, 
            connectionId: connection.id,
            error: connectionError.message 
          };
        }
      });

      // Wait for all refresh operations
      const results = await Promise.all(refreshPromises);
      
      const successfulRefreshes = results.filter(r => r.success);
      const failedRefreshes = results.filter(r => !r.success);
      
      console.log(`📊 Manual refresh results: ${successfulRefreshes.length} successful, ${failedRefreshes.length} failed`);
      
      // Return success if at least one connection was refreshed successfully
      if (successfulRefreshes.length > 0) {
        return { success: true };
      } else {
        const errors = failedRefreshes.map(r => r.error).join(', ');
        return { success: false, error: `All connections failed to refresh: ${errors}` };
      }
    } catch (error) {
      console.error(`💥 Error during manual token refresh for user ${userId}:`, error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }
}
